// QueryStateGate — honest render states for a query-backed region.
//
// The dominant render bug found by the idle-wake audit
// (docs/audits/IDLE-WAKE-DEADLOCK-ROOTCAUSE-2026-07-02.md, §5): 64% of the
// app's 845 useQuery destructures take only `data` with a default, so pending,
// paused (offline / wedged onlineManager) and error all render the page's
// fallback — usually a confident "No X yet" empty state, several with
// create-CTAs that invite duplicate capture. In react-query v5 the paused
// state renders that lie INSTANTLY (`isLoading = isPending && isFetching`;
// paused ≠ fetching).
//
// Usage — wrap only the no-rows branch; keep rendering your list/detail from
// (possibly stale) data as usual so background refetches never blank the page:
//
//   const runsQ = useQuery({ ... })
//   const runs = runsQ.data ?? []
//   ...
//   {runs.length === 0 ? (
//     <QueryStateGate query={runsQ} noun="runs"
//       empty={<p className="text-gray-500">No runs yet</p>} />
//   ) : ( ...list... )}
//
// ⚠️ Rendered OUTSIDE that branch — beside the list — it prints its "No X yet"
// ABOVE the real rows, because a usable read renders `empty` (see below). Four
// packhouse pages did exactly that, one of them "No lots match" above the
// matched lots of a recall trace (packhouse audit PM-SH-12, PW-15). Where the
// gate sits beside the list, pass `isEmpty={rows.length === 0}` instead: false
// renders nothing, so the claim can only ever appear over no rows.
//
// Skeleton loading (opt-in): pass `skeleton={{ rows: 4, rowClassName: 'h-24 rounded-xl' }}`
// and the pending branch shows grey placeholder rows (SkeletonRows, a status
// region named `Loading <noun>…`) instead of the centred BrandLoader. Use it
// for a LIST or CARD area whose page chrome (header, filters) is already on
// screen, and match `rowClassName` to the real row's height. Never on a
// whole-page gate (that stays on BrandLoader, check-page-loader-rings) and
// never on a detail view. The "Still trying" line still shows under it.
// Absent = today's BrandLoader render, unchanged.
//
// The `empty` node — the only branch allowed to claim "there is genuinely
// nothing here" — renders whenever we HOLD an answer, i.e. the query has data
// (an empty array is an answer). It is never rendered for a read that has
// produced nothing. Detail pages/modals use the same shape with
// `empty={<...not found...>}` so "deleted?" is never shown for a fetch that
// merely failed.
//
// Note "holds data" is deliberately not "the last fetch succeeded": a failed
// background refetch leaves the data in place, and a known answer must not
// degrade into an error card. See the DATA WINS note on the branch itself.

import { ReactNode, useEffect, useState } from 'react'
import { isNetworkish } from '../lib/offlineQueue/classify'
import { readErrorText } from '../lib/readError'
import { readState } from '../lib/readState'
import { BrandLoader, LoadingWords } from '@/components/BrandLoader'
import { SkeletonRows } from '@/components/Skeleton'

// A read that failed because the link is down/timed out, or because the
// auth-token refresh hasn't landed yet (the guardedFetch anon-guard block —
// supabase.ts) is a CONNECTIVITY problem, not a fault in the data. The
// field-normal case: a scout on marginal signal whose token expired, so the
// authed read would otherwise run as anon. We surface it as the same calm
// "offline" note as a truly-paused query rather than a red engineer string
// ("AbortError: … blocked: auth refresh pending …"). Unlike the paused branch
// this query is genuinely in `error`, so Retry is a real re-run, not a no-op —
// the reconnect / auth-heal refetch also recovers it on its own. Reuses the
// offline queue's own predicate so read + write classify the same fault
// identically; AbortError/TimeoutError names cover the DOMExceptions whose
// message the REST/auth nets shape without "failed to fetch".
function looksOffline(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null
  if (e?.name === 'AbortError' || e?.name === 'TimeoutError') return true
  return typeof e?.message === 'string' && isNetworkish(e.message)
}

// Structural subset of UseQueryResult so callers can pass the query object
// straight through without generics (and tests can pass a plain object).
export interface QueryStateLike {
  isPending:  boolean
  isFetching: boolean
  isError:    boolean
  isPaused:   boolean
  error:      unknown
  refetch:    () => unknown
  // Optional so hand-built combiners still type-check. Absent means "no data",
  // which lands on the pre-existing status branches — the safe default.
  data?:      unknown
}

// Past this, a healthy fetch has long since resolved (the REST net caps at
// 30s) — tell the operator we're still at it so a slow/stuck link reads as
// "connection problem", not "app frozen".
const STILL_TRYING_MS = 35_000

// Surface palettes. The default 'dark' is the app shell these states were
// written for. 'light' is for PRINTABLE reports (white article on gray-100):
// rendering the dark tones there produces red-300 text on a pale pink card,
// which a 2026-08-10 render of the estimates report showed is barely legible —
// and an unreadable error on a document that leaves the building reads as a
// blank section, not as a failure.
const SURFACE = {
  dark: {
    amberBox: 'card border border-amber-700/40 bg-amber-900/10',
    amberTxt: 'text-amber-300',
    redBox:   'card border border-red-700/40 bg-red-900/10',
    redTitle: 'text-red-300',
    redBody:  'text-red-200/80',
    muted:    'text-gray-400',
  },
  light: {
    amberBox: 'rounded-lg border border-amber-400 bg-amber-50',
    amberTxt: 'text-amber-900',
    redBox:   'rounded-lg border border-red-400 bg-red-50',
    redTitle: 'text-red-900',
    redBody:  'text-red-800',
    muted:    'text-muted',
  },
} as const

export type QueryStateSurface = keyof typeof SURFACE

// One branch per render state IS the component; unit-covered in
// QueryStateGate.test.tsx (the CI gate runs without coverage data).
// fallow-ignore-next-line complexity
export default function QueryStateGate({ query, noun, empty, surface = 'dark', isEmpty, skeleton }: {
  query: QueryStateLike
  /** Plural, lower-case, for default copy: "runs", "deliveries", "pallets". */
  noun:  string
  /** Rendered ONLY on confirmed success — the honest "No X yet" (or "not found"). */
  empty: ReactNode
  /** 'light' for printable reports. Defaults to the dark app shell. */
  surface?: QueryStateSurface
  /** Whether the rows rendered beside this gate are empty. Pass it when the gate
   *  is NOT inside your no-rows branch: `false` renders nothing at all, so an
   *  empty claim can never sit above real rows. Omit it inside the branch. */
  isEmpty?: boolean
  /** Opt in to grey placeholder rows while loading instead of the centred
   *  BrandLoader. For a list/card area under already-visible page chrome only. */
  skeleton?: { rows?: number; rowClassName?: string }
}) {
  const s = SURFACE[surface]
  const loading = query.isPending && query.isFetching && !query.isPaused
  const [stillTrying, setStillTrying] = useState(false)
  useEffect(() => {
    if (!loading) { setStillTrying(false); return }
    const t = setTimeout(() => setStillTrying(true), STILL_TRYING_MS)
    return () => clearTimeout(t)
  }, [loading])

  // ⚠️ DATA WINS OVER BOTH (2026-08-25). Every branch below asks how the last
  // FETCH went; none of them asked whether we actually hold an answer. So a
  // query that genuinely returned zero rows, and whose next background refetch
  // then failed, rendered "Couldn't load {noun}" — replacing a fact we are sure
  // of ("there are none") with a shrug. In react-query v5 that is the common
  // case, not a corner: a failed refetch reports `status:'error'` while `data`
  // stays exactly where it was (see src/lib/readState.ts for the proof).
  //
  // The caller only renders this gate on its no-rows branch, so reaching here
  // WITH data means the read succeeded and the answer is genuinely empty. Say
  // so. Staleness is the caller's to note beside the region — this component's
  // job is to never turn a known answer into an error.
  //
  // Found by the Phase B review of the labour sweep: the scan feed painted a
  // pulsing green "live · updated 05:02:11" header directly above this
  // component's red "couldn't load" card, asserting both in the same 40 pixels.
  // The caller says rows are on screen: nothing to add, in any state — the rows
  // themselves are the answer (a failed refetch keeps them, per DATA WINS).
  if (isEmpty === false) return null
  if (readState(query) === 'usable') return <>{empty}</>

  // Paused wins over error: a failed query being retried while offline is an
  // offline problem, and "Retry" while paused is a silent no-op (the refetch
  // just parks paused again).
  if (query.isPaused) {
    return (
      <div className={`${s.amberBox} p-4`}>
        <p className={`text-sm ${s.amberTxt}`}>
          Offline — {noun} will load when the connection returns
        </p>
      </div>
    )
  }
  if (loading && skeleton) {
    return (
      <div>
        <SkeletonRows rows={skeleton.rows} rowClassName={skeleton.rowClassName} label={`Loading ${noun}…`} />
        {stillTrying && <p className={`text-xs ${s.muted} text-center pt-3`}>Still trying — check the connection…</p>}
      </div>
    )
  }
  if (loading) {
    return (
      <div className="flex flex-col items-center gap-3 py-12">
        <BrandLoader className="w-8 h-8" />
        {stillTrying
          ? <p className={`text-xs ${s.muted}`}>Still trying — check the connection…</p>
          : <LoadingWords className={`text-xs ${s.muted}`} />}
      </div>
    )
  }
  if (query.isError) {
    // A connectivity / auth-refresh fault reads as calm "offline" (amber) with
    // a real Retry — not the red "something is broken" card reserved for a
    // genuine server/data error the operator should notice.
    if (looksOffline(query.error)) {
      return (
        <div className={`${s.amberBox} p-4 space-y-2`}>
          <p className={`text-sm ${s.amberTxt}`}>
            Offline — {noun} will load when the connection returns
          </p>
          {/* type="button": this gate renders inside capture <form>s (Modal
              onSubmit) — a bare button would submit the form instead. */}
          <button
            type="button"
            onClick={() => void query.refetch()}
            disabled={query.isFetching}
            className="text-xs px-3 py-1.5 coarse:min-h-[44px] coarse:px-4 rounded-lg bg-amber-700 text-white hover:bg-amber-600 disabled:opacity-50">
            {query.isFetching ? 'Retrying…' : 'Retry'}
          </button>
        </div>
      )
    }
    // The read path goes through the same mapping as the write path
    // (readErrorText → friendlyError). This line interpolated `error.message`
    // verbatim, which is how `relation "chemical_balances" does not exist`
    // reached a farm manager on 13 of 14 registers. No Details disclosure here:
    // unlike a failed save, nobody asked for this card, and main.tsx already
    // reports every failed read IN FULL from the QueryCache — support has the
    // driver text, so the screen doesn't need it (src/lib/readError.ts).
    return (
      <div className={`${s.redBox} p-4 space-y-2`}>
        <p className={`text-sm font-semibold ${s.redTitle}`}>Couldn't load {noun}</p>
        <p className={`text-xs ${s.redBody} break-words`}>{readErrorText(query.error)}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          disabled={query.isFetching}
          className="text-xs px-3 py-1.5 coarse:min-h-[44px] coarse:px-4 rounded-lg bg-red-700 text-white hover:bg-red-600 disabled:opacity-50">
          {query.isFetching ? 'Retrying…' : 'Retry'}
        </button>
      </div>
    )
  }
  return <>{empty}</>
}
